import React, { useState } from 'react';

import { defineMessages } from 'react-intl';

import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { usePref } from 'src/hooks/use-pref';

const messages = defineMessages({
  markAsReadySpotlightHeader: {
    id: 'frontbucket.pullRequest.markAsReadySpotlight.header',
    description: 'Header for mark as ready spotlight',
    defaultMessage: 'Notify reviewers when your pull request is ready',
  },
  markAsReadySpotlightBody: {
    id: 'frontbucket.pullRequest.markAsReadySpotlight.body',
    description: 'Body for mark as ready spotlight',
    defaultMessage:
      "When you've added any code or other content you want to include in your draft pull request, mark your pull request as ready and we will notify your reviewers and move it out of draft mode.",
  },
  markAsDraftSpotlightHeader: {
    id: 'frontbucket.pullRequest.markAsDraftSpotlight.header',
    description: 'Header for mark as draft spotlight',
    defaultMessage: "There's always the option to go back to draft",
  },
  markAsDraftSpotlightBody: {
    id: 'frontbucket.pullRequest.markAsDraftSpotlight.body',
    description: 'Body for mark as draft spotlight',
    defaultMessage:
      "In case you accidentally or weren't ready to move your pull request out of draft mode, you can mark it as a draft again to work in silence. Any new reviewers will be notified when you mark it as ready again, but any previously added reviewers will not be notified again.",
  },
  dismiss: {
    id: 'frontbucket.pullRequest.button.dismiss',
    description: 'Text to display on the button to dismiss the spotlight tour.',
    defaultMessage: 'Dismiss',
  },
  next: {
    id: 'frontbucket.pullRequest.button.next',
    description:
      'Text to display on the button to go to the next spotlight tour step.',
    defaultMessage: 'Next',
  },
  goBack: {
    id: 'frontbucket.pullRequest.button.goBack',
    description:
      'Text to display on the button to go back to the previous spotlight tour step.',
    defaultMessage: 'Go back',
  },
});

export const DraftPullRequestSpotlight = (): JSX.Element | null => {
  const intl = useIntl();
  const areDraftPRsEnabled = useStatsigGate(StatsigFeatureKeys.bbcDraftPRs);
  const [activeSpotlight, setActiveSpotlight] = useState<null | number>(0);
  const [spotlightTourSeen, updatePref, isLoading] = usePref(
    'pr-draft-onboarding-2-seen'
  );

  if (
    !areDraftPRsEnabled ||
    spotlightTourSeen ||
    isLoading ||
    activeSpotlight === null
  ) {
    return null;
  }

  const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);

  const back = () => setActiveSpotlight((activeSpotlight || 1) - 1);

  const handleDismiss = () => {
    updatePref('true');
    setActiveSpotlight(null);
  };

  const renderActiveSpotlight = () => {
    const spotlights = [
      <Spotlight
        heading={intl.formatMessage(messages.markAsReadySpotlightHeader)}
        key="mark-as-ready-for-review"
        target="mark-as-ready-for-review"
        actions={[
          {
            onClick: next,
            text: intl.formatMessage(messages.next),
          },
          {
            onClick: handleDismiss,
            text: intl.formatMessage(messages.dismiss),
            appearance: 'subtle',
          },
        ]}
        dialogPlacement="bottom right"
        actionsBeforeElement="1/2"
      >
        {intl.formatMessage(messages.markAsReadySpotlightBody)}
      </Spotlight>,
      <Spotlight
        heading={intl.formatMessage(messages.markAsDraftSpotlightHeader)}
        key="mark-as-draft"
        target="mark-as-draft"
        actions={[
          {
            onClick: handleDismiss,
            text: intl.formatMessage(messages.dismiss),
          },
          {
            onClick: back,
            text: intl.formatMessage(messages.goBack),
            appearance: 'subtle',
          },
        ]}
        dialogPlacement="bottom right"
        actionsBeforeElement="2/2"
      >
        {intl.formatMessage(messages.markAsDraftSpotlightBody)}
      </Spotlight>,
    ];

    if (activeSpotlight === null) {
      return null;
    }

    return spotlights[activeSpotlight];
  };

  return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};
